import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close,
  ExpandMore,
  Psychology as ThoughtIcon,
  Refresh,
  Timeline as TimelineIcon,
  SmartToy as BotIcon,
  Build as ToolIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { fetchChatExecutionTrace } from '../../services/executionTraceApi';

const formatTs = (val) => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(val);
  }
};

const formatDuration = (ms) => {
  if (ms == null || ms === '') return null;
  const n = Number(ms);
  if (Number.isNaN(n)) return null;
  if (n <= 0) return '<1 ms';
  if (n < 1000) return `${Math.round(n)} ms`;
  return `${(n / 1000).toFixed(1)} s`;
};

/** Timeline markers — logged for context, not separately timed (duration lives on the next op step). */
const MARKER_STEP_TYPES = new Set([
  'REQUEST_RECEIVED',
  'LLM_START',
  'TOKEN_USAGE',
  'RUN_METADATA',
  'FINAL_ANSWER',
  'ERROR',
  // Inclusive bookmarks (duration in message/data; not summed into Ops)
  'SYNC_SETUP',
  'RESPONSE_READY',
]);

const statusColor = (status) => {
  const s = (status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'COMPLETED' || s === 'OK') return 'success';
  if (s === 'FAILED' || s === 'ERROR') return 'error';
  if (s === 'RUNNING' || s === 'STARTED') return 'info';
  return 'default';
};

const prettyJson = (value) => {
  if (value == null || value === '') return '';
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(value);
  }
};

const parseJson = (value) => {
  if (value == null || value === '') return null;
  try {
    return typeof value === 'string' ? JSON.parse(value) : value;
  } catch {
    return null;
  }
};

const shortId = (id) => {
  if (!id) return '—';
  const s = String(id);
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
};

const getStepIcon = (stepType) => {
  const t = (stepType || '').toUpperCase();
  if (t.includes('THOUGHT')) {
    return <ThoughtIcon sx={{ fontSize: 18, color: '#7b1fa2' }} />;
  }
  if (t.includes('TOOL') || t.includes('MCP') || t.includes('N8N')) {
    return <ToolIcon sx={{ fontSize: 18, color: '#1976d2' }} />;
  }
  return <BotIcon sx={{ fontSize: 18, color: '#6B0F2A' }} />;
};

const PayloadBlock = ({ label, value, maxHeight = 360 }) => {
  if (value == null || value === '') return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        component="pre"
        variant="caption"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'monospace',
        }}
      >
        {prettyJson(value)}
      </Typography>
    </Box>
  );
};

const extractThought = (details) => {
  const d = parseJson(details) || details;
  if (!d || typeof d !== 'object') return null;
  return d.thought || d.raw_log || null;
};

/** Step types where duration_ms is a real measured operation (not a timeline marker). */
const TIMED_STEP_TYPES = new Set([
  'MCP_INVOKED',
  'LLM_RESPONSE',
  'AGENT_THOUGHT',
  'MEMORY_LOADED',
  'VECTOR_CONTEXT_LOADED',
  'TOOL_CALL',
  // plain_llm Sync path (exclusive spans; LLM_RESPONSE = post-TTFT generation when streamed)
  'CONTEXT_PRELOAD',
  'PROMPT_BUILT',
  'LLM_TTFT',
]);

const isTimedStepType = (stepType) => TIMED_STEP_TYPES.has((stepType || '').toUpperCase());

const stepDurationMs = (step) => {
  const stepType = step.step_type ?? step.stepType ?? '';
  if (!isTimedStepType(stepType)) return null;
  const direct = step.duration_ms ?? step.durationMs;
  if (direct != null && direct !== '') {
    const n = Number(direct);
    return Number.isNaN(n) ? null : n;
  }
  const d = parseJson(step.details ?? step.detail ?? step.data) || step.details;
  if (d && typeof d === 'object' && d.duration_ms != null) {
    const n = Number(d.duration_ms);
    return Number.isNaN(n) ? null : n;
  }
  return null;
};

const sumTimedStepDurations = (steps) =>
  (steps || []).reduce((sum, step) => {
    const ms = stepDurationMs(step);
    return ms != null ? sum + ms : sum;
  }, 0);

const ChatStepAccordion = ({ step, index }) => {
  const stepNo = step.step_no ?? step.stepNo ?? index + 1;
  const stepType = step.step_type ?? step.stepType ?? 'STEP';
  const stepStatus = step.step_status ?? step.stepStatus ?? 'UNKNOWN';
  const durationMs = stepDurationMs(step);
  const details = step.details ?? step.detail ?? step.data;
  const thought = (stepType || '').toUpperCase().includes('THOUGHT')
    ? extractThought(details)
    : extractThought(details);

  return (
    <Accordion
      defaultExpanded={index < 2}
      disableGutters
      sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
          {getStepIcon(stepType)}
          <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
            {stepNo}. {stepType.replace(/_/g, ' ')}
          </Typography>
          {durationMs != null && (
            <Chip label={formatDuration(durationMs)} size="small" variant="outlined" />
          )}
          {MARKER_STEP_TYPES.has((stepType || '').toUpperCase()) && (
            <Chip label="marker" size="small" variant="outlined" sx={{ opacity: 0.65 }} />
          )}
          {isTimedStepType(stepType) && durationMs == null && (
            <Chip label="not recorded" size="small" variant="outlined" sx={{ opacity: 0.65 }} />
          )}
          <Chip label={stepStatus} size="small" color={statusColor(stepStatus)} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: 'grey.50' }}>
        {step.message && (
          <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
            {step.message}
          </Typography>
        )}
        {thought && (
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'rgba(123, 31, 162, 0.06)',
              border: '1px solid rgba(123, 31, 162, 0.2)',
            }}
          >
            <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
              Agent reasoning
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {typeof thought === 'string' ? thought : prettyJson(thought)}
            </Typography>
          </Box>
        )}
        {(step.model_name || step.prompt_tokens != null) && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
            {step.model_name && <Chip size="small" label={`Model: ${step.model_name}`} variant="outlined" />}
            {step.prompt_tokens != null && (
              <Chip size="small" label={`Prompt: ${step.prompt_tokens}`} variant="outlined" />
            )}
            {step.completion_tokens != null && (
              <Chip size="small" label={`Completion: ${step.completion_tokens}`} variant="outlined" />
            )}
            {step.total_tokens != null && (
              <Chip size="small" label={`Total: ${step.total_tokens}`} variant="outlined" />
            )}
            {details?.scope && (
              <Chip size="small" label={`Scope: ${details.scope}`} variant="outlined" />
            )}
            {details?.cycle != null && (
              <Chip size="small" label={`Call ${details.cycle}`} variant="outlined" />
            )}
          </Stack>
        )}
        {Array.isArray(details?.segments) && details.segments.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
              Token breakdown by part
            </Typography>
            <Stack spacing={1}>
              {details.segments.map((seg, i) => (
                <Box
                  key={`${seg?.name || 'seg'}-${i}`}
                  sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={600}>
                      {seg?.name || 'part'}
                    </Typography>
                    <Chip size="small" label={`${seg?.tokens ?? 0} tokens`} />
                  </Stack>
                  {seg?.content != null && seg.content !== '' && (
                    <Typography
                      component="pre"
                      variant="caption"
                      sx={{
                        m: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflow: 'auto',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      }}
                    >
                      {String(seg.content)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
        <PayloadBlock label="Details" value={details} />
      </AccordionDetails>
    </Accordion>
  );
};

const McpLogAccordion = ({ log, index }) => {
  const steps = log.mcp_steps ?? log.mcpSteps ?? [];
  const toolName = log.tool_name || log.operation_key || log.endpoint_key || 'MCP call';

  return (
    <Accordion
      defaultExpanded={index === 0}
      disableGutters
      sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
          <ToolIcon sx={{ fontSize: 18, color: '#1976d2' }} />
          <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
            {toolName}
          </Typography>
          <Chip
            label={log.status || log.execution_status || '—'}
            size="small"
            color={statusColor(log.status || log.execution_status)}
          />
          {log.http_status_code != null && (
            <Chip label={`HTTP ${log.http_status_code}`} size="small" variant="outlined" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: 'grey.50' }}>
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            MCP ID: {shortId(log.mcp_execution_id)}
          </Typography>
          {log.duration_ms != null && (
            <Typography variant="caption" color="text.secondary">
              Duration: {formatDuration(log.duration_ms)}
            </Typography>
          )}
        </Stack>
        {log.error_message && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {log.error_message}
          </Alert>
        )}
        <PayloadBlock label="Request" value={log.request_payload} />
        <PayloadBlock label="Response" value={log.response_payload} />
        {steps.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              MCP steps ({steps.length})
            </Typography>
            {steps.map((s, i) => (
              <ChatStepAccordion
                key={s.step_id ?? s.step_no ?? i}
                step={{
                  step_no: s.step_no ?? i + 1,
                  step_type: s.step_type ?? s.component ?? 'MCP_STEP',
                  step_status: s.step_status ?? 'SUCCESS',
                  message: s.message,
                  details: s.details,
                }}
                index={i}
              />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const TaskLogAccordion = ({ log, index }) => {
  const steps = parseJson(log.executionSteps);
  const stepList = Array.isArray(steps) ? steps : steps?.steps && Array.isArray(steps.steps) ? steps.steps : [];
  const mcpLogs = log.mcpLogs ?? log.mcp_logs ?? [];

  return (
    <Accordion
      defaultExpanded={index === 0}
      disableGutters
      sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
          {log.status === 'SUCCESS' ? (
            <SuccessIcon sx={{ fontSize: 18, color: 'success.main' }} />
          ) : (
            <ErrorIcon sx={{ fontSize: 18, color: log.status === 'FAILED' ? 'error.main' : 'text.secondary' }} />
          )}
          <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
            {log.actionName || 'Task run'} · {formatTs(log.startedAt)}
          </Typography>
          <Chip label={log.status || '—'} size="small" color={statusColor(log.status)} />
          {log.durationMs != null && (
            <Chip label={formatDuration(log.durationMs)} size="small" variant="outlined" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: 'grey.50' }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
          {log.modelName && <Chip size="small" label={`Model: ${log.modelName}`} variant="outlined" />}
          {log.totalTokens != null && (
            <Chip size="small" label={`Tokens: ${log.totalTokens}`} variant="outlined" />
          )}
        </Stack>

        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Execution ID: {log.executionId || '—'}
          </Typography>
          {log.requestId && (
            <Typography variant="caption" color="text.secondary">
              Request ID: {log.requestId}
            </Typography>
          )}
          {log.correlationId && (
            <Typography variant="caption" color="text.secondary">
              Correlation: {log.correlationId}
            </Typography>
          )}
        </Stack>

        {log.summary && (
          <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
            {log.summary}
          </Typography>
        )}

        {log.errorMessage && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {log.errorMessage}
          </Alert>
        )}

        {log.llmResponseText && (
          <PayloadBlock label="LLM response" value={log.llmResponseText} />
        )}

        <PayloadBlock label="Request payload" value={log.requestPayload} />
        <PayloadBlock label="Response payload" value={log.responsePayload} />
        <PayloadBlock label="LLM decisions / tool calls" value={log.llmDecisions} />

        {stepList.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Execution steps ({stepList.length})
            </Typography>
            {stepList.map((step, i) => (
              <ChatStepAccordion
                key={step.step_no ?? step.stepNo ?? i}
                step={{
                  step_no: step.step_no ?? step.stepNo ?? i + 1,
                  step_type: step.step_type ?? step.stepType ?? step.type,
                  step_status: step.step_status ?? step.status ?? step.stepStatus,
                  message: step.message,
                  details: step.details ?? step.data ?? step,
                  model_name: step.model_name,
                  prompt_tokens: step.prompt_tokens,
                  completion_tokens: step.completion_tokens,
                  total_tokens: step.total_tokens,
                  duration_ms: step.duration_ms ?? step.durationMs,
                }}
                index={i}
              />
            ))}
          </Box>
        )}

        {mcpLogs.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              MCP tool calls ({mcpLogs.length})
            </Typography>
            {mcpLogs.map((mcpLog, i) => (
              <McpLogAccordion key={mcpLog.mcp_execution_id ?? i} log={mcpLog} index={i} />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const ExecutionTraceDrawer = ({ open, onClose, requestId, title = 'Run trace' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trace, setTrace] = useState(null);

  const load = useCallback(async () => {
    if (!requestId) {
      setTrace(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchChatExecutionTrace(requestId);
      setTrace(data);
    } catch (e) {
      setError(e.message || 'Failed to load run trace');
      setTrace(null);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (open && requestId) {
      load();
    }
    if (!open) {
      setTrace(null);
      setError(null);
    }
  }, [open, requestId, load]);

  const steps = trace?.chat_steps ?? [];
  const mcpLogs = trace?.mcp_logs ?? [];
  const totalWallMs =
    trace?.duration_ms != null ? Number(trace.duration_ms) : null;
  const timedSumMs = sumTimedStepDurations(steps);
  const overheadMs =
    totalWallMs != null && timedSumMs > 0
      ? Math.max(0, Math.round(totalWallMs - timedSumMs))
      : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 640 } } }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: '#6B0F2A',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <TimelineIcon />
        <Typography variant="h6" sx={{ flex: 1, fontSize: '1.05rem' }}>
          {title}
        </Typography>
        <IconButton size="small" onClick={load} disabled={loading || !requestId} sx={{ color: 'white' }}>
          <Refresh fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
        {!requestId && (
          <Alert severity="info">No run trace is linked to this message yet.</Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#6B0F2A' }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && trace && (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip label={trace.status || 'UNKNOWN'} size="small" color={statusColor(trace.status)} />
              {totalWallMs != null && !Number.isNaN(totalWallMs) && (
                <Chip
                  label={`Wall clock ${formatDuration(totalWallMs)}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {timedSumMs > 0 && (
                <Chip
                  label={`Ops ${formatDuration(Math.round(timedSumMs))}`}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip label={`Req ${shortId(trace.request_id)}`} size="small" variant="outlined" />
            </Stack>

            {timedSumMs > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Durations on LLM response, agent thought, and MCP tool steps are measured wall time for
                that operation only. LLM START and request markers are not timed separately.
                {totalWallMs != null && !Number.isNaN(totalWallMs) && (
                  <>
                    {' '}
                    Wall clock ({formatDuration(totalWallMs)}) includes streaming, hub, and logging
                    {overheadMs != null && overheadMs >= 50
                      ? ` (~${formatDuration(overheadMs)} beyond timed ops).`
                      : '.'}
                  </>
                )}
              </Typography>
            )}

            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Started: {formatTs(trace.started_at)}
              </Typography>
              {trace.completed_at && (
                <Typography variant="caption" color="text.secondary">
                  Completed: {formatTs(trace.completed_at)}
                </Typography>
              )}
              {trace.correlation_id && (
                <Typography variant="caption" color="text.secondary">
                  Correlation: {trace.correlation_id}
                </Typography>
              )}
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Execution steps ({steps.length})
            </Typography>

            {steps.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No execution steps recorded for this run.
              </Alert>
            ) : (
              steps.map((step, index) => (
                <ChatStepAccordion key={step.log_id ?? step.step_no ?? index} step={step} index={index} />
              ))
            )}

            {mcpLogs.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  MCP tool calls ({mcpLogs.length})
                </Typography>
                {mcpLogs.map((mcpLog, index) => (
                  <McpLogAccordion key={mcpLog.mcp_execution_id ?? index} log={mcpLog} index={index} />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export { TaskLogAccordion, ChatStepAccordion, McpLogAccordion };
export default ExecutionTraceDrawer;
